import React, { useState } from 'react';
import Breadcrumbs from '~/components/Breadcrumbs';
import CompaniesAlphabeticalList from '~/components/Companies/CompaniesAlphabeticalList';
import Container from '~/components/ui/Container';
import DefaultLayout from '~/layouts/DefaultLayout';
import type { ProviderSummary, ProviderScope } from '~/types/Provider';
import type { BreadcrumbLink } from '~/types/common/Breadcrumb';
import { Message } from '~/components/ui/Message';
import CompanyListItemTag from '~/components/Companies/CompanyListItemTag';
import ProviderFilters from '~/components/Companies/ProviderFilters';
import { useProviderFilters } from '~/components/Companies/useProviderFilters';
import { cn } from '~/helpers/utils';
import OpenFiltersButton from '~/components/Companies/OpenFiltersButton';

export type ProviderIndexProps = {
  providers: ProviderSummary[];
  uniqueScopes: ProviderScope[];
};

export const ProviderIndex: React.FC<ProviderIndexProps> = ({ providers, uniqueScopes }) => {
  const {
    setSearch,
    selectedScopes,
    setSelectedScopes,
    filteredProviders,
  } = useProviderFilters(providers);

  const [open, setOpen] = useState<boolean>(false);
  
  const breadcrumbLinks: BreadcrumbLink[] = [
    { name: 'Home', linkTo: '/' },
    { name: 'Aanbieders', linkTo: '/over/aanbieders' },
  ];

  return (
    <DefaultLayout breadcrumbs={<Breadcrumbs links={breadcrumbLinks} />}>
      <Container className="pb-16 md:pb-24">
        <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-10 md:gap-10">
          <div className="md:col-span-3" />
          <div className="md:col-span-7">
            <h1 className="mb-2.5 font-avantgarde text-lg font-bold text-blue-700 md:mb-3 md:text-2xl">
              Aanbieders
            </h1>
            <h2 className="mb-4 font-avantgarde text-base font-light text-blue-500 md:mb-6 md:text-lg">
              Dit is een overzicht van alle aanbieders
            </h2>
          </div>
        </div>

        <div className="relative -mx-[2.5%] md:mx-0 mb-8">
          <div className="relative grid grid-cols-1 gap-4 md:grid-cols-10 md:gap-10">
            <div 
              className={cn(
                'z-10 absolute bg-white top-0 bottom-0 w-[80vw] transition-transform duration-300 ease-in-out shadow-lg p-6',
                'md:col-span-3 md:relative md:w-full md:translate-x-0 md:shadow-none md:p-0 md:duration-0',
                open ? 'translate-x-0' : '-translate-x-full',
              )}
            >
              <ProviderFilters
                setSearch={setSearch}
                selectedScopes={selectedScopes}
                setSelectedScopes={setSelectedScopes}
                uniqueScopes={uniqueScopes}
              />
            </div>
            <div className="min-h-[740px] px-4 md:px-0 md:col-span-7">
              {filteredProviders.length > 0 ? (
                <CompaniesAlphabeticalList
                  path="/over/aanbieder"
                  companies={filteredProviders as ProviderSummary[]}
                >
                  {(item) => (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {item.scopes.map((s) => (
                        <CompanyListItemTag key={s.id}>{s.name}</CompanyListItemTag>
                      ))}
                    </div>
                  )}
                </CompaniesAlphabeticalList>
              ) : (
                <Message>Er zijn helaas geen passende aanbieders gevonden.</Message>
              )}
            </div>
          </div>
          <OpenFiltersButton open={open} setOpen={setOpen} />
        </div>
      </Container>
    </DefaultLayout>
  );
};
